import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import PageShell from "../../components/layout/PageShell";
import PageHead from "../../components/layout/PageHead";
import KpiRail from "../../components/dashboard/KpiRail";
import NetInflowPanel from "../../components/dashboard/NetInflowPanel";
import QueuePanel from "../../components/dashboard/QueuePanel";
import ActivityPanel from "../../components/dashboard/ActivityPanel";
import StatusPanel from "../../components/dashboard/StatusPanel";
import CampaignsTable, { SHOW_LIVE_CAMPAIGNS_TABLE } from "../../components/dashboard/CampaignsTable";
import { useRole } from "../../hooks/useRole";
import StaffHome from "./StaffHome";

const formatUtc = (d) =>
  `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}:${String(d.getUTCSeconds()).padStart(2, "0")} UTC`;

const Home = () => {
  const { roles, hasRole, canDecide } = useRole();
  // A platform manager lands on the operations dashboard even when they also
  // hold a compliance role: oversight is what the AD role is for. Anyone else
  // who can decide lands in their review workspace, and CONSULTANT sessions
  // belong in the BDC workspace, so they land there exactly like a BDC.
  const isAD = hasRole("AD");
  const goDR = !isAD && canDecide;
  const goBDC = !isAD && !canDecide && hasRole("BDC", "CONSULTANT");
  // Any other resolved staff seat (for example a marketing EMPLOYEE) gets the
  // staff home: the operations figures below are platform-wide and AD-only. A
  // session with no resolved role keeps the pre-existing admin dashboard.
  const goStaff = !isAD && !goDR && !goBDC && roles.length > 0;

  const [userStats, setUserStats] = useState(null);
  const [campaignStats, setCampaignStats] = useState(null);
  const [investmentSummary, setInvestmentSummary] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);

  useEffect(() => {
    if (goDR || goBDC || goStaff) return;
    const token = localStorage.getItem("accessToken");
    const headers = { "x-access-token": token };
    const baseUrl = process.env.REACT_APP_API;

    Promise.allSettled([
      axios.get(baseUrl + "get-users-count", { headers }),
      axios.get(baseUrl + "get-campaign-count", { headers }),
      axios.get(baseUrl + "admin/get-investments-summary", { headers }),
      axios.get(baseUrl + "admin/get-payment-status-summary", { headers }),
    ]).then(([usersRes, campaignRes, investRes, paymentRes]) => {
      if (usersRes.status === "fulfilled") {
        setUserStats(usersRes.value.data.data.user_count);
      }
      if (campaignRes.status === "fulfilled") {
        setCampaignStats(campaignRes.value.data.data.campaign_count);
      }
      if (investRes.status === "fulfilled") {
        setInvestmentSummary(investRes.value.data.data);
      }
      if (paymentRes.status === "fulfilled") {
        setPaymentStatus(paymentRes.value.data.data);
      }
      setRefreshedAt(new Date());
    });
  }, []);

  if (goDR) return <Navigate to="/dr" replace />;
  if (goBDC) return <Navigate to="/bdc" replace />;
  if (goStaff) return <StaffHome />;

  const subtitle = (
    <>
      <span>Last refresh</span>
      <span className="mono">{refreshedAt ? formatUtc(refreshedAt) : "—"}</span>
      <span>·</span>
      <span>Currency <span className="mono">CAD</span></span>
    </>
  );

  return (
    <PageShell>
      <PageHead title="Operations" subtitle={subtitle} />
      <KpiRail
        userStats={userStats}
        campaignStats={campaignStats}
        investmentSummary={investmentSummary}
        paymentStatus={paymentStatus}
      />
      <div className="grid-2">
        <NetInflowPanel yearlyData={investmentSummary?.yearlyData} />
        <QueuePanel
          campaignStats={campaignStats}
          userStats={userStats}
          paymentStatus={paymentStatus}
        />
      </div>
      {SHOW_LIVE_CAMPAIGNS_TABLE && <CampaignsTable />}
      <div className="grid-2">
        <ActivityPanel />
        <StatusPanel campaignStats={campaignStats} />
      </div>
    </PageShell>
  );
};

export default Home;
